// src/components/fraud/SuspendedUserViewDrawer.js
// Read-only slide-in detail drawer for a suspended user.
// Identical slide-in pattern as FraudLogDrawer and BlacklistViewDrawer.

import React, { useState, useCallback } from 'react';
import { parseSuDate } from '../../hooks/useSuspendedUsers';

// ─── Copy hook ────────────────────────────────────────────────────────────────
const useCopy = () => {
  const [copied, setCopied] = useState(null);
  const copy = useCallback((text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key); setTimeout(() => setCopied(null), 1800);
    });
  }, []);
  return { copied, copy };
};

// ─── Shared atoms (identical pattern to FraudLogDrawer) ──────────────────────
const CopyBtn = ({ text, label, copied, onCopy }) => {
  const isCopied = copied === text;
  return (
    <button
      title={isCopied ? 'Copied!' : `Copy ${label || ''}`}
      onClick={() => onCopy(text, text)}
      style={{ background: 'transparent', border: 'none', padding: '0 4px', cursor: 'pointer', color: isCopied ? '#22C55E' : 'var(--text-secondary)', fontSize: 12, verticalAlign: 'middle', flexShrink: 0 }}
    >
      {isCopied ? '✓' : '⧉'}
    </button>
  );
};

const Field = ({ label, value, mono = false, copyable = false, copy, copied }) => {
  if (value === null || value === undefined || value === '') return null;
  const str = String(value);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-secondary)', marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, color: 'var(--text-primary)', fontSize: 13, fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>
        <span style={{ flex: 1 }}>{str}</span>
        {copyable && copy && <CopyBtn text={str} label={label} copied={copied} onCopy={copy} />}
      </div>
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--accent-lime)', borderBottom: '1px solid var(--divider)', paddingBottom: 6, marginBottom: 14, marginTop: 20 }}>
    {children}
  </div>
);

const ScoreBadge = ({ score }) => {
  if (score === null || score === undefined)
    return <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>No data</span>;
  const color = score >= 70 ? '#EF4444' : score >= 40 ? '#F59E0B' : '#22C55E';
  return (
    <span style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>
      {score}
      <small style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 4 }}>/ 100</small>
    </span>
  );
};

const BoolBadge = ({ value, trueLabel = 'Yes', falseLabel = 'No' }) => (
  <span style={{ fontSize: 11, fontWeight: 700, color: value ? '#22C55E' : '#EF4444', background: value ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.10)', border: `1px solid ${value ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.25)'}`, borderRadius: 20, padding: '2px 10px' }}>
    {value ? trueLabel : falseLabel}
  </span>
);

// ─── Main Drawer ──────────────────────────────────────────────────────────────
const SuspendedUserViewDrawer = ({ user, open, onClose, onReinstate, onViewLogs, onBlacklist }) => {
  const { copied, copy } = useCopy();
  if (!user) return null;

  const fmt = (d) => {
    const date = d instanceof Date ? d : parseSuDate(d);
    if (!date) return '—';
    return date.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1040, opacity: open ? 1 : 0, transition: 'opacity 0.25s ease', pointerEvents: open ? 'all' : 'none' }}
      />

      {/* Drawer panel */}
      <div
        id="suspended-user-drawer"
        style={{ position: 'fixed', top: 0, right: 0, height: '100vh', width: 'min(520px, 92vw)', background: 'var(--bg-surface)', borderLeft: '1px solid var(--divider)', zIndex: 1050, transform: open ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 32px rgba(0,0,0,0.4)' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--divider)', flexShrink: 0 }}>
          <div>
            <h6 style={{ margin: 0, color: 'var(--text-primary)', fontWeight: 700, fontSize: 15 }}>Suspended User</h6>
            <small style={{ color: 'var(--text-secondary)', fontSize: 11, fontFamily: 'monospace' }}>{user.uid}</small>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--divider)', borderRadius: 6, color: 'var(--text-primary)', width: 32, height: 32, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

          {/* ── Overview row ── */}
          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', marginBottom: 4 }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 4 }}>Fraud Score</div>
              <ScoreBadge score={user.fraudScore} />
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 4 }}>Email Verified</div>
              <BoolBadge value={user.isEmailVerified} />
            </div>
          </div>

          <SectionTitle>Identity</SectionTitle>
          <Field label="UID"          value={user.uid}         mono copyable copy={copy} copied={copied} />
          <Field label="Name"         value={user.name} />
          <Field label="Email"        value={user.email}       copyable copy={copy} copied={copied} />
          <Field label="Phone Number" value={user.phoneNumber} copyable copy={copy} copied={copied} />
          <Field label="Auth Provider" value={user.authProvider} />

          <SectionTitle>Account</SectionTitle>
          <Field label="Status"     value={user.status} />
          <Field label="Created At" value={fmt(user.createdAt)} />
          <Field label="Last Login" value={fmt(user.lastLogin)} />

          <SectionTitle>Fraud Profile</SectionTitle>
          <Field label="Fraud Score"           value={user.fraudScore !== null ? String(user.fraudScore) : null} />
          <Field label="High Risk Payments"    value={user.highRiskPayments > 0 ? String(user.highRiskPayments) : null} />
          <Field label="Last Payment"          value={fmt(user.lastPayment)} />
          <Field label="Last Card Fingerprint" value={user.lastCardFingerprint} mono />
          <Field label="Fraud Profile Updated" value={fmt(user.fraudProfileUpdatedAt)} />

          <SectionTitle>Suspension</SectionTitle>
          <Field label="Suspended At"     value={fmt(user.suspendedAt)} />
          <Field label="Suspended By"     value={user.suspendedBy} />
          <Field label="Suspension Reason" value={user.suspensionReason} />

          {/* ── Quick actions ── */}
          <SectionTitle>Actions</SectionTitle>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={() => { onClose(); setTimeout(() => onReinstate(user), 200); }}
              style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, color: '#22C55E', fontSize: 12, fontWeight: 600, padding: '7px 14px', cursor: 'pointer' }}
            >
              ✓ Reinstate
            </button>
            <button
              onClick={() => { onClose(); onViewLogs(user); }}
              style={{ background: 'rgba(139,92,246,0.10)', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 8, color: '#8B5CF6', fontSize: 12, fontWeight: 600, padding: '7px 14px', cursor: 'pointer' }}
            >
              📋 Fraud Logs
            </button>
            <button
              onClick={() => { onClose(); setTimeout(() => onBlacklist(user), 200); }}
              style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, color: '#EF4444', fontSize: 12, fontWeight: 600, padding: '7px 14px', cursor: 'pointer' }}
            >
              🚫 Blacklist
            </button>
          </div>

          <div style={{ height: 32 }} />
        </div>
      </div>
    </>
  );
};

export default SuspendedUserViewDrawer;
